import React, { useState, useEffect } from 'react';
import { Calculator, Star, Users } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const CustomerSatisfactionScoreCalculator = () => {
  const [totalResponses, setTotalResponses] = useState(100);
  const [satisfiedResponses, setSatisfiedResponses] = useState(80);
  const [cssScore, setCssScore] = useState(0);

  useEffect(() => {
    calculateCSSScore();
  }, [totalResponses, satisfiedResponses]);

  const calculateCSSScore = () => {
    const score = (satisfiedResponses / totalResponses) * 100;
    setCssScore(parseFloat(score.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/customer-satisfaction-score-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>CSAT Calculator: Measure Customer Satisfaction Score | Free Tool</title>
        <meta name="description" content="Calculate your Customer Satisfaction Score (CSAT) instantly. Compare with industry benchmarks, get optimization tips, and improve customer happiness." />
        <meta name="keywords" content="CSAT calculator, customer satisfaction score, satisfaction metrics, customer experience analytics, customer feedback" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free CSAT Calculator: Boost Customer Satisfaction" />
        <meta property="og:description" content="Calculate your Customer Satisfaction Score instantly. Get actionable insights to improve customer happiness and loyalty." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free CSAT Calculator: Boost Customer Satisfaction" />
        <meta property="twitter:description" content="Calculate your Customer Satisfaction Score instantly. Get actionable insights to improve customer happiness and loyalty." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Customer Satisfaction Score Calculator",
            "description": "Free calculator to measure and improve customer satisfaction scores (CSAT)",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate CSAT Score",
              "Industry Benchmarks",
              "Satisfaction Improvement Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Customer Satisfaction Score",
              "description": "A metric that measures customer satisfaction with a business, product, or service based on survey responses."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Customer Experience Managers, Business Owners, Service Providers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Customer Satisfaction Score",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total number of survey responses"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input number of satisfied responses (4-5 stars)"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your CSAT score and improvement tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Customer Satisfaction Score (CSAT) Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Responses</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalResponses}
                  onChange={(e) => setTotalResponses(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="100"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Satisfied Responses (4 or 5 stars)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Star className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={satisfiedResponses}
                  onChange={(e) => setSatisfiedResponses(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="80"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {cssScore}%
            <Star className="w-8 h-8 ml-2 text-yellow-400" />
          </div>
          <p className="text-gray-600">Customer Satisfaction Score (CSAT)</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Customer Satisfaction Score (CSAT) is {cssScore}%. This means that {cssScore}% of your customers are satisfied with your product or service.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Retail: 75-85%</li>
          <li>Technology: 80-85%</li>
          <li>Banking: 75-80%</li>
          <li>Healthcare: 70-80%</li>
          <li>Hospitality: 80-85%</li>
          <li>E-commerce: 75-85%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Satisfaction Improvement Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Collect and act on feedback regularly</li>
          <li>Improve customer service quality</li>
          <li>Personalize customer interactions</li>
          <li>Set clear expectations</li>
          <li>Implement loyalty programs</li>
          <li>Continuously improve products/services</li>
          <li>Train staff effectively</li>
          <li>Address negative feedback promptly</li>
          <li>Create seamless experiences</li>
          <li>Proactively communicate with customers</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact on Business Metrics</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>5% increase in CSAT can lead to 25% revenue growth</li>
          <li>Satisfied customers are 3x more likely to recommend</li>
          <li>87% of satisfied customers make repeat purchases</li>
          <li>Positive experiences shared with 9-10 people on average</li>
          <li>Customer retention 5x higher with high CSAT scores</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Best Practices</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Survey at key touchpoints</li>
          <li>Keep surveys short and focused</li>
          <li>Follow up on feedback</li>
          <li>Track trends over time</li>
          <li>Segment satisfaction data</li>
          <li>Compare with other metrics (NPS, CES)</li>
          <li>Share insights across teams</li>
        </ul>
      </div>
    </div>
  );
};

export default CustomerSatisfactionScoreCalculator;
